import { Component, OnInit, Output, EventEmitter, Input} from '@angular/core';
import { StylesStatus } from 'app/models/users/StylesStatus';
import { TableLazyLoadEvent } from 'primeng/table';
import { DriverCommerceOverview, DriverCommerce, DriverCommerceDto, DriverCommerceStatus } from './driver-commerce-dto';
import { CommerceService } from '../../../../../services/commerce.service';
import { MessageService } from 'primeng/api';
import { Utils } from 'app/utils';

@Component({
  selector: 'driver-commerce',
  templateUrl: './driver-commerce.component.html',
  styleUrl: './driver-commerce.component.scss',
  standalone: false
})
export class DriverCommerceComponent implements OnInit {

  @Output() showTransaction = new EventEmitter<boolean>();
  @Output() showDetails = new EventEmitter<number>();
  @Input() riderId!: number;

  @Input() driver: any;

  constructor(
    private commerceService: CommerceService,
    private messageService: MessageService
  ){}

  public productsAvailedCols: any[] = [
    { field: 'Status', header: 'Status', width: '20%' },
    { field: 'ProgramType', header: 'Program Type', width: '20%' },
    { field: 'ProductId', header: 'Product ID', width: '20%' },
    { field: 'ProductName', header: 'Product Name', width: '20%' },
    { field: 'DateAvailed', header: 'Date Availed', width: '20%' },
    { field: 'StartDate', header: 'Start Date', width: '20%' },
    { field: 'ProductPrice', header: 'Amount', width: '20%' }
  ];

  public rowsPerPage: number = 10;
  public rowsPerPageOptions: Array<number> = [10, 20, 30];
  public totalRecords: number = -1;

  public first: number = 0;
  public sortField: string | string[] = '';
  private sortOrder: number = 1;
  public lazyLoadHandled: boolean = false;
  public loading: boolean = false;
  public driverCommerce: DriverCommerce[] = [];
  public commerceOverview!: DriverCommerceOverview;

  public filter: {
    searchName: string;
  } = { searchName: '' };
  private searchTimer: number = 0;
  public isAddProductVisible: boolean = false;
  public isUploadProductsVisible: boolean = false;

  ngOnInit(): void {
    if (!this.lazyLoadHandled) {
      this.handleLazyLoad({
          first: 0,
          rows: this.rowsPerPage
      });
    }
    this.getCommerceOverview();
    this.getDriverCommerce(0);
  }

  public getStatusStyles(status: any): StylesStatus {
    let backgroundColor: string;
    let textColor: string;
    switch (status) {
        case DriverCommerceStatus.PendingTopup:
            backgroundColor = '#FFF2D9';
            textColor = '#997328';
            break;
        case DriverCommerceStatus.PendingRelease:
            backgroundColor = '#FFC043';
            textColor = '#33260D';
            break;
        case DriverCommerceStatus.Active:
            backgroundColor = '#D2F1E2';
            textColor = '#0B4A2D';
            break;
        case DriverCommerceStatus.Paused:
            backgroundColor = '#6B7280';
            textColor = '#FFF';
            break;
        case DriverCommerceStatus.Suspended:
            backgroundColor = '#CCD9DF';
            textColor = '#EB1D25';
            break;
        case DriverCommerceStatus.Cancelled:
            backgroundColor = '#FBD2D3';
            textColor = '#EB1D25';
            break;
        case DriverCommerceStatus.Completed:
            backgroundColor = '#1CBA70';
            textColor = '#FFF';
            break;
        // case DriverCommerceStatus.ForCollections:
        //     backgroundColor = '#EB1D25';
        //     textColor = '#FFF';
        //     break;
        default:
            backgroundColor = '';
            textColor = '';
    }

    return {
      backgroundColor,
      color: textColor
    };
  }

  public getStatusText(status: any): string {
    let statusText = '';
    switch (status) {
      case DriverCommerceStatus.PendingTopup:
          statusText = 'Pending Top-up';
          break;
      case DriverCommerceStatus.PendingRelease:
        statusText = 'Pending Release';
          break;
      case DriverCommerceStatus.Active:
        statusText = 'Active';
          break;
      case DriverCommerceStatus.Paused:
        statusText = 'Paused';
          break;
      case DriverCommerceStatus.Suspended:
        statusText = 'Suspended';
          break;
      case DriverCommerceStatus.Cancelled:
        statusText = 'Cancelled';
          break;
      case DriverCommerceStatus.Completed:
        statusText = 'Completed';
          break;
      default:
        statusText = '';
    }
    return statusText;
  }

  public handleLazyLoad(event: TableLazyLoadEvent): void {
      const skip = event.first ? event.first : 0;

      if (event.rows) {
          this.rowsPerPage = event.rows;
      }

      if (!event.sortField) {
        this.sortField = 'Id';
        this.sortOrder = -1; // Desc
      }
      else {
          this.sortField = event.sortField;
          this.sortOrder = event.sortOrder ? event.sortOrder : 1;
      }
      this.getDriverCommerce(skip);

      this.lazyLoadHandled = false;
  }

  public showTransactions() {
    this.showTransaction.emit(true);
  }

  public getProductDetails(commerceId: number) {
    this.showDetails.emit(commerceId);
  }

  public getDriverCommerce(skip: number): void {
    this.loading = true;
    const dto: DriverCommerceDto =  {
        SearchName: this.filter.searchName,
        Skip: skip,
        Take: this.rowsPerPage,
        SortField: this.sortField,
        SortOrder: this.sortOrder,
        RiderId: this.riderId
    };
    this.commerceService.getDriverCommerce(dto, this.messageService)
        .then(data => {
            if (!data.Success) {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                return;
            }
            this.first = skip;
            this.totalRecords = data.TotalRecords;
            this.driverCommerce = data.Items;
            this.driverCommerce.forEach(item => {
                item.StartDate = Utils.utcToLocaleString(item.StartDate);
                item.DateAvailed = Utils.utcToLocaleString(item.DateAvailed);
            });
            this.loading = false;
        })
        .catch(err => {
            this.loading = false;
            throw err;
        });
}

public getCommerceOverview(): void {
  this.loading = true;
  const dto = {
      RiderId: this.riderId
  };
  this.commerceService.getCommerceOverview(dto, this.messageService)
      .then(data => {
          if (!data.Success) {
              this.loading = false;
              this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
              return;
          }

          if(data.Overview){
            this.commerceOverview = data.Overview;
            this.commerceOverview.FirstTXDate = Utils.utcToLocaleString(this.commerceOverview.FirstTXDate);
            this.commerceOverview.LastDeduction = Utils.utcToLocaleString(this.commerceOverview.LastDeduction);
            this.commerceOverview.NextDeduction = Utils.utcToLocaleString(this.commerceOverview.NextDeduction);
          }

          this.loading = false;
      })
      .catch(err => {
          this.loading = false;
          throw err;
      });
  }

  public handleFilterChange(): void {
    if (this.searchTimer) {
        window.clearTimeout(this.searchTimer);
    }

    this.searchTimer = window.setTimeout(() => {
        this.getDriverCommerce(0);
        this.searchTimer = 0;
    }, 1500);
  }

  showAddProduct(){
    this.isAddProductVisible = true;
  }

  hideAddProduct(event: boolean){
    this.isAddProductVisible = event;
  }

  hideProduct(event: boolean){
    this.isAddProductVisible = event;
    this.getCommerceOverview();
    this.getDriverCommerce(0);
  }

  showUploadProducts(){
    this.isUploadProductsVisible = true;
  }

  hideUploadProducts(event: boolean){
    this.isUploadProductsVisible = event;
  }

  onUploadSuccess(file: File){
    // TODO: Implement file upload service call
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Products file uploaded successfully. Processing...'
    });
    // Refresh data after upload
    this.getCommerceOverview();
    this.getDriverCommerce(0);
  }
}
